import { Component, ElementRef, Inject, OnInit, ViewChild } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';

export interface FileDropDialogData {
    title: string;
    /** `accept` attribute for the fallback file input */
    accept: string;
    /** allowed extensions, lowercase and without the dot */
    fileTypes: string[];
    maxFileSizeMb: number;
    /** false keeps a single file selected - the last one dropped or picked */
    multiple?: boolean;
}

interface SelectedFile {
    file: File;
    error: string | null;
}

@Component({
    selector: 'kt-file-drop-dialog',
    templateUrl: './file-drop-dialog.component.html',
    styleUrls: ['./file-drop-dialog.component.scss']
})
export class FileDropDialogComponent implements OnInit {
    @ViewChild('fileInput') fileInput: ElementRef<HTMLInputElement>;

    selectedFiles: SelectedFile[] = [];

    constructor(
        public dialogRef: MatDialogRef<FileDropDialogComponent>,
        @Inject(MAT_DIALOG_DATA) public data: FileDropDialogData
    ) {
    }

    ngOnInit(): void {
    }

    get multiple(): boolean {
        return this.data.multiple !== false;
    }

    get validFiles(): File[] {
        return this.selectedFiles.filter(f => f.error === null).map(f => f.file);
    }

    get hasInvalidFiles(): boolean {
        return this.selectedFiles.some(f => f.error !== null);
    }

    get allowedTypesLabel(): string {
        return this.data.fileTypes.join(', ');
    }

    browse() {
        this.fileInput.nativeElement.click();
    }

    onInputChange(event: any) {
        this.addFiles(Array.from(event.target.files as FileList));
        event.srcElement.value = null;
    }

    onFilesDropped(files: File[]) {
        this.addFiles(files);
    }

    removeFile(index: number) {
        this.selectedFiles.splice(index, 1);
    }

    formatSize(size: number): string {
        if (size < 1024) {
            return `${size} B`;
        }
        if (size < 1024 * 1024) {
            return `${(size / 1024).toFixed(1)} KB`;
        }
        return `${(size / (1024 * 1024)).toFixed(1)} MB`;
    }

    onCancel() {
        this.dialogRef.close();
    }

    onUpload() {
        const files = this.validFiles;
        if (files.length === 0) {
            return;
        }
        this.dialogRef.close(files);
    }

    private addFiles(files: File[]) {
        if (!this.multiple) {
            const file = files[0];
            this.selectedFiles = file ? [{file, error: this.validate(file)}] : [];
            return;
        }
        files.forEach(file => {
            const alreadyAdded = this.selectedFiles
                .some(f => f.file.name === file.name && f.file.size === file.size);
            if (alreadyAdded) {
                return;
            }
            this.selectedFiles.push({file, error: this.validate(file)});
        });
    }

    private validate(file: File): string | null {
        const ext = file.name.split('.').pop().toLowerCase();
        if (!this.data.fileTypes.find(e => e === ext)) {
            return `Only ${this.allowedTypesLabel} files are allowed`;
        }
        if (file.size > this.data.maxFileSizeMb * 1024 * 1024) {
            return `Larger than ${this.data.maxFileSizeMb}mb`;
        }
        return null;
    }
}
